import { Wallet } from "lucide-react";
import { useCallback } from "react";

import { InputSelect } from "@/shared/components/form/InputSelect";
import useTranslations from "@/shared/lang/useTranslations";

import type { CurrencyOption } from "../utils/currencyOptions.utils";
import { formatQuoterAmount, parseAmountInput } from "../utils/formatQuoterAmount.utils";
import type { QuoterMode } from "./QuoterStep1";

type QuoterStep2Props = {
  mode: QuoterMode;
  currencyOptions: CurrencyOption[];
  originCurrency: CurrencyOption | null;
  destinationCurrency: CurrencyOption | null;
  amount: string;
  commission: string;
  onOriginChange: (opt: CurrencyOption | null) => void;
  onDestinationChange: (opt: CurrencyOption | null) => void;
  onAmountChange: (value: string) => void;
  onCommissionChange: (value: string) => void;
  isLoading?: boolean;
};

export const QuoterStep2 = ({
  mode,
  currencyOptions,
  originCurrency,
  destinationCurrency,
  amount,
  commission,
  onOriginChange,
  onDestinationChange,
  onAmountChange,
  onCommissionChange,
  isLoading = false,
}: QuoterStep2Props) => {
  const { t } = useTranslations("quoter");

  const formatAmountOnBlur = useCallback(() => {
    const num = parseAmountInput(amount);
    if (num > 0) onAmountChange(formatQuoterAmount(num));
  }, [amount, onAmountChange]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const allowed = v.replace(/[^\d.,]/g, "");
    const parts = allowed.split(/[,.]/);
    if (parts.length > 2) {
      const decimalPart = (parts[parts.length - 1] ?? "").replace(/\D/g, "").slice(0, 2);
      const integerPart = parts.slice(0, -1).join("").replace(/\D/g, "");
      const normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      onAmountChange(normalized);
    } else if (parts.length === 2 && parts[1].length > 2) {
      onAmountChange(parts[0] + (allowed.includes(",") ? "," : ".") + parts[1].slice(0, 2));
    } else {
      onAmountChange(allowed);
    }
  };

  const amountLabel = mode === "receive" ? t("step2_amount_receive") : t("step2_amount_send");
  const amountFieldCurrencyIso =
    mode === "receive" ? (destinationCurrency?.value ?? "—") : (originCurrency?.value ?? "COP");

  return (
    <div className="lg:col-span-7 flex flex-col gap-6">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400">
        {t("step2_title")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            {t("step2_origin_label")}
          </label>
          <InputSelect<CurrencyOption>
            options={currencyOptions}
            value={originCurrency}
            onChange={(v) => onOriginChange(v as CurrencyOption | null)}
            placeholder={t("step2_select_currency")}
            isDisabled={isLoading}
            maxWidth="max-w-full"
            controlClassName="!rounded-xl !border !border-gray-200 dark:!border-zinc-600 !bg-gray-50 dark:!bg-zinc-800 !py-3 !min-h-[48px]"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            {t("step2_destination_label")}
          </label>
          <InputSelect<CurrencyOption>
            options={currencyOptions}
            value={destinationCurrency}
            onChange={(v) => onDestinationChange(v as CurrencyOption | null)}
            placeholder={t("step2_select_currency")}
            isDisabled={isLoading}
            maxWidth="max-w-full"
            controlClassName="!rounded-xl !border !border-gray-200 dark:!border-zinc-600 !bg-gray-50 dark:!bg-zinc-800 !py-3 !min-h-[48px]"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-zinc-300 flex items-center gap-1">
            <Wallet className="size-4 text-emerald-500" />
            {amountLabel}
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              onBlur={formatAmountOnBlur}
              placeholder="Ej: 3.000.00"
              className="w-full bg-white dark:bg-zinc-800 border-2 border-emerald-500/20 dark:border-emerald-500/10 rounded-xl py-4 px-5 text-xl font-bold focus:ring-2 focus:ring-green-500 focus:border-green-500 placeholder:text-gray-300 dark:placeholder:text-zinc-600 transition-all"
            />
            <span className="absolute right-5 top-1/2 -translate-y-1/2 font-bold text-gray-400 dark:text-zinc-400 pointer-events-none">
              {amountFieldCurrencyIso}
            </span>
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            {t("step2_commission_label")}
          </label>
          <div className="relative flex items-center">
            <input
              type="text"
              inputMode="decimal"
              value={commission}
              onChange={(e) => {
                const v = e.target.value;
                const allowed = v.replace(/[^\d.,]/g, "");
                const parts = allowed.split(/[,.]/);
                const sep = allowed.match(/[,.]/)?.[0] ?? ".";
                const valid = parts.length <= 2 ? allowed : parts[0] + sep + (parts[1] ?? "");
                onCommissionChange(valid);
              }}
              placeholder="0"
              className="w-full bg-gray-50 dark:bg-zinc-800 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-green-500 font-medium"
            />
            <span className="absolute right-4 text-gray-400 font-bold pointer-events-none">%</span>
          </div>
        </div>
      </div>
    </div>
  );
};
